import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import axios from 'axios';

import csrfHeaders from '../utils/csrfHeaders';

const buildErrorMessage = (error, fallbackMessage) => (
  error?.response?.data?.error || fallbackMessage
);

const useReferralEntityMatches = () => {
  const [matches, setMatches] = useState([]);
  const [sourceReferralEntity, setSourceReferralEntity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [combining, setCombining] = useState(false);
  const [error, setError] = useState(null);
  const fetchRequestIdRef = useRef(0);
  const combineRequestIdRef = useRef(0);
  const fetchAbortControllerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
    fetchAbortControllerRef.current?.abort();
  }, []);

  const fetchMatches = useCallback(async (fileToken) => {
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;
    const isLatestRequest = () => mountedRef.current && fetchRequestIdRef.current === requestId;

    fetchAbortControllerRef.current?.abort();

    if (!fileToken) {
      if (isLatestRequest()) {
        setMatches([]);
        setSourceReferralEntity(null);
        setError(null);
        setLoading(false);
      }
      return [];
    }

    const controller = new AbortController();
    fetchAbortControllerRef.current = controller;

    if (isLatestRequest()) {
      setLoading(true);
      setError(null);
      setMatches([]);
      setSourceReferralEntity(null);
    }

    try {
      const response = await axios.get(
        `/api/v1/records/${encodeURIComponent(fileToken)}/referral_entity_matches`,
        { signal: controller.signal },
      );
      const nextMatches = response.data?.matches || [];
      if (isLatestRequest()) {
        setMatches(nextMatches);
        setSourceReferralEntity(response.data?.source_referral_entity || null);
      }
      return nextMatches;
    } catch (requestError) {
      if (axios.isCancel?.(requestError)) return [];
      if (isLatestRequest()) {
        setMatches([]);
        setSourceReferralEntity(null);
        setError(buildErrorMessage(requestError, 'Failed to load suggested referrals'));
      }
      return [];
    } finally {
      if (isLatestRequest()) setLoading(false);
    }
  }, []);

  const combine = useCallback(async ({
    targetReferralSlug,
    reviewedDocumentTokens = [],
    selectedDocumentTokens = [],
    primaryRecordToken,
    reviewedPrimaryRecordToken,
  }) => {
    const requestId = combineRequestIdRef.current + 1;
    combineRequestIdRef.current = requestId;
    const isLatestRequest = () => mountedRef.current && combineRequestIdRef.current === requestId;
    const selectedTokens = [...new Set(selectedDocumentTokens.filter(Boolean))];
    const reviewedTokens = [...new Set(reviewedDocumentTokens.filter(Boolean))];

    if (!targetReferralSlug || selectedTokens.length === 0) {
      const validationMessage = 'A referral match must be selected before linking';
      if (isLatestRequest()) setError(validationMessage);
      return { error: validationMessage };
    }

    if (isLatestRequest()) {
      setCombining(true);
      setError(null);
    }

    try {
      const response = await axios.post(
        `/api/v1/referral_entities/${encodeURIComponent(targetReferralSlug)}/link_review`,
        {
          primary_record_token: primaryRecordToken || null,
          reviewed_primary_record_token: reviewedPrimaryRecordToken || null,
          reviewed_document_tokens: reviewedTokens,
          selected_document_tokens: selectedTokens,
        },
        { headers: csrfHeaders() },
      );

      return { data: response.data };
    } catch (requestError) {
      const errorMessage = buildErrorMessage(requestError, 'Failed to link referral');
      // 422 with stale_tokens means the modal's view of linked records was
      // stale (e.g. another user unlinked one). Surface the offending tokens
      // so callers can prune them from the local selection and let the user
      // retry without losing their combine intent.
      const staleTokens = requestError?.response?.data?.stale_tokens || [];
      if (isLatestRequest()) setError(errorMessage);
      return { error: errorMessage, staleTokens };
    } finally {
      if (isLatestRequest()) setCombining(false);
    }
  }, []);

  return {
    fetchMatches,
    combine,
    matches,
    sourceReferralEntity,
    loading,
    combining,
    error,
  };
};

export default useReferralEntityMatches;
